package co.maytes.standupbot.dashboard.features.integrations

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.SegmentedControl
import co.maytes.standupbot.dashboard.components.widgets.SegmentedOption
import co.maytes.standupbot.dashboard.features.settings.AnthropicKeyCopy
import co.maytes.standupbot.dashboard.features.settings.Brand
import co.maytes.standupbot.dashboard.features.settings.BrandLogo
import co.maytes.standupbot.dashboard.features.settings.ClaudeModel
import co.maytes.standupbot.dashboard.features.settings.IntegrationsSecretInput
import co.maytes.standupbot.dashboard.features.settings.KeyNoteTone
import co.maytes.standupbot.dashboard.features.settings.SettingsField
import co.maytes.standupbot.dashboard.features.settings.SettingsRowItem
import co.maytes.standupbot.dashboard.features.settings.SettingsSaveBar
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.dto.appcredentials.AnthropicAppCredentialsDto
import co.maytes.standupbot.shared.dto.appcredentials.AppCredentialPreviewDto
import co.maytes.standupbot.shared.dto.appcredentials.AppCredentialProviderDto
import co.maytes.standupbot.shared.dto.appcredentials.KeyCheckRules
import co.maytes.standupbot.shared.dto.appcredentials.KeyStatus
import co.maytes.standupbot.shared.dto.appcredentials.TestConnectionResultDto
import co.maytes.standupbot.shared.dto.integration.HealthStateDto
import co.maytes.standupbot.shared.dto.integration.HealthTone
import co.maytes.standupbot.shared.dto.integration.IntegrationDto
import co.maytes.standupbot.shared.dto.integration.effectiveState
import co.maytes.standupbot.shared.dto.integration.healthBadge
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.CSSColorValue
import kotlin.js.Date

@Composable
fun AnthropicPanel(
    workspaceId: String?,
    store: AppCredentialsStore,
    health: IntegrationDto? = null,
) {
    val provider = AppCredentialProviderDto.ANTHROPIC
    val preview = store.preview(provider)
    val testResult = store.lastTestResult(provider)
    val action = store.panelAction(provider)
    var editing by remember { mutableStateOf(false) }
    var apiKey by remember { mutableStateOf("") }
    var selectedModel by remember(preview?.model) { mutableStateOf(ClaudeModel.fromId(preview?.model).id) }
    var check by remember { mutableStateOf<TestConnectionResultDto?>(null) }
    var checkedFor by remember { mutableStateOf<Pair<String, String>?>(null) }
    val scope = rememberCoroutineScope()
    val ui = rememberUiStore()

    val activeCheck = check.takeIf { checkedFor == apiKey.trim() to selectedModel }
    val present = preview?.present == true
    val locallyRejected = testResult != null && !testResult.ok
    val effective = health.takeUnless { locallyRejected }
    val needsAttention = effective != null && effective.effectiveState() != HealthStateDto.HEALTHY
    val healthSummary = effective?.health?.summary?.takeIf { it.isNotBlank() }
    val previewLine = preview?.takeIf { it.present }?.let(::buildPreviewLine)
    val note = AnthropicKeyCopy.note(activeCheck)

    fun closeForm() {
        editing = false
        apiKey = ""
        check = null
        checkedFor = null
    }

    fun saveKey(verified: TestConnectionResultDto?) {
        scope.launch {
            if (workspaceId.isNullOrBlank()) {
                ui.showToast("No workspace. Are you signed in?", ToastKind.Error)
                return@launch
            }
            val body = AnthropicAppCredentialsDto(apiKey = apiKey.trim(), model = selectedModel)
            when (val res = store.saveAnthropic(workspaceId, body, verified)) {
                is Result.Ok -> {
                    closeForm()
                    ui.showToast(
                        if (verified != null) "Anthropic key verified and saved." else "Anthropic key saved but not verified.",
                        ToastKind.Success,
                    )
                }
                is Result.Err -> ui.showToast("Save failed: ${res.error.displayMessage}", ToastKind.Error)
            }
        }
    }

    fun checkKey() {
        scope.launch {
            if (workspaceId.isNullOrBlank()) {
                ui.showToast("No workspace. Are you signed in?", ToastKind.Error)
                return@launch
            }
            val target = apiKey.trim() to selectedModel
            val body = AnthropicAppCredentialsDto(apiKey = target.first, model = target.second)
            when (val res = store.validateAnthropic(workspaceId, body)) {
                is Result.Ok -> {
                    check = res.value
                    checkedFor = target
                }
                is Result.Err -> ui.showToast("Couldn't check the key: ${res.error.displayMessage}", ToastKind.Error)
            }
        }
    }

    fun testStoredKey() {
        scope.launch {
            workspaceId?.let {
                when (val r = store.test(it, provider)) {
                    is Result.Ok -> ui.showToast(
                        if (r.value.ok) "Anthropic connection OK." else "Anthropic test failed: ${r.value.detail ?: "unknown"}",
                        if (r.value.ok) ToastKind.Success else ToastKind.Error,
                    )
                    is Result.Err -> ui.showToast("Test failed: ${r.error.displayMessage}", ToastKind.Error)
                }
            }
        }
    }

    Column(modifier = Modifier.id("integration-anthropic").fillMaxWidth()) {
        SettingsRowItem(
            title = "Anthropic API key",
            first = true,
            description = healthSummary ?: previewLine ?: AnthropicKeyCopy.NO_KEY,
            leading = { BrandLogo(Brand.Claude, dimmed = !present) },
            titleTrailing = { StatusBadge(preview = preview, testResult = testResult, health = effective) },
            trailing = {
                if (present) {
                    Button(
                        label = if (action == PanelAction.Testing) "Testing…" else "Test connection",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        enabled = action == PanelAction.Idle,
                        onClick = ::testStoredKey,
                    )
                }
                if (!editing) {
                    Button(
                        label = effective?.health?.fix?.label?.takeIf { needsAttention }
                            ?: if (present) "Replace key" else "Add key",
                        variant = if (needsAttention) ButtonVariant.Primary else ButtonVariant.Secondary,
                        size = ButtonSize.Sm,
                        onClick = { editing = true },
                    )
                }
            },
        )

        if (healthSummary != null && previewLine != null) KeyLine(text = previewLine, color = Palette.textTertiary)
        if (testResult != null && !testResult.ok) {
            KeyLine(text = testResult.detail ?: AnthropicKeyCopy.UNVERIFIED, color = Palette.status(StatusHue.Danger).solid)
        }
    }

    if (editing) {
        SettingsField(
            label = "API key",
            help = AnthropicKeyCopy.KEY_HELP,
            error = note?.takeIf { it.tone == KeyNoteTone.Danger }?.text,
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier { property("gap", Spacing.sm.toString()) },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                IntegrationsSecretInput(
                    value = apiKey,
                    onValueChange = { apiKey = it },
                    modifier = Modifier.styleModifier { property("flex", "1 1 auto") },
                    placeholder = "sk-ant-…",
                    label = "API key",
                )
                Button(
                    label = if (action == PanelAction.Validating) "Checking…" else "Check key",
                    variant = ButtonVariant.Secondary,
                    size = ButtonSize.Sm,
                    enabled = apiKey.isNotBlank() && action == PanelAction.Idle,
                    onClick = ::checkKey,
                )
                if (KeyCheckRules.offersSaveAnyway(activeCheck)) {
                    Button(
                        label = "Save anyway",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        enabled = action == PanelAction.Idle,
                        onClick = { saveKey(verified = null) },
                    )
                }
            }
        }
        note?.takeIf { it.tone == KeyNoteTone.Success }?.let {
            KeyLine(text = it.text, color = Palette.status(StatusHue.Success).solid)
        }
        SettingsField(label = "Model", help = ClaudeModel.fromId(selectedModel).blurb) {
            SegmentedControl(
                options = MODEL_OPTIONS,
                selected = ClaudeModel.fromId(selectedModel),
                onSelect = { selectedModel = it.id },
                ariaLabel = "Model",
            )
        }
    }

    SettingsSaveBar(
        open = editing,
        saving = action == PanelAction.Saving,
        hint = AnthropicKeyCopy.saveHint(checking = action == PanelAction.Validating, check = activeCheck),
        canSave = KeyCheckRules.canSave(activeCheck) && action == PanelAction.Idle,
        saveLabel = "Save key",
        onSave = { saveKey(verified = activeCheck) },
        onReset = ::closeForm,
    )
}

@Composable
private fun KeyLine(text: String, color: CSSColorValue) {
    SpanText(
        text = text,
        modifier = Modifier
            .margin(top = Spacing.xs)
            .fontSize(Typography.bodySmall.size)
            .color(color),
    )
}

@Composable
private fun StatusBadge(
    preview: AppCredentialPreviewDto?,
    testResult: TestConnectionResultDto?,
    health: IntegrationDto?,
) {
    if (health != null) {
        val badge = health.healthBadge()
        Badge(
            label = badge.label,
            tone = when (badge.tone) {
                HealthTone.SUCCESS -> BadgeTone.Success
                HealthTone.WARNING -> BadgeTone.Warning
                HealthTone.ERROR -> BadgeTone.Error
                HealthTone.NEUTRAL -> BadgeTone.Neutral
            },
        )
        return
    }
    val (tone, label) = when (KeyCheckRules.status(preview?.present == true, testResult)) {
        KeyStatus.NOT_CONFIGURED -> BadgeTone.Warning to "Not configured"
        KeyStatus.CONNECTED -> BadgeTone.Success to "Connected"
        KeyStatus.REJECTED -> BadgeTone.Error to "Not verified"
        KeyStatus.NOT_VERIFIED -> BadgeTone.Neutral to "Not verified"
    }
    Badge(label = label, tone = tone)
}

private fun buildPreviewLine(p: AppCredentialPreviewDto): String = buildList {
    p.clientIdTail?.let { add("client_id …$it") }
    p.appId?.let { add("app_id $it") }
    p.model?.let { add(ClaudeModel.fromId(it).label) }
    p.oauthRedirectUri?.takeIf { it.isNotBlank() }?.let { add("redirect $it") }
    p.updatedAt?.let { add("updated ${formatInstant(it)}") }
}.joinToString(" · ")

private fun formatInstant(instant: kotlinx.datetime.Instant): String {
    val ms = instant.toEpochMilliseconds().toDouble()
    val d = Date(ms)
    return d.toLocaleDateString()
}

private val MODEL_OPTIONS = ClaudeModel.entries.map { SegmentedOption(it, it.label) }
